import React, { useEffect, useRef, useState, lazy, Suspense } from "react";
import "./Navbar.css";
import SearchIcon from "@mui/icons-material/Search";
import Logo from "../../shared/images/logo.png";
import MenuTransitions from "./MenuTransitions";
import { Link, useNavigate, useLocation } from "react-router-dom";
import MegaMenu from "./MegaMenu";
import { useDispatch, useSelector } from "react-redux";
import { openCartModal, closeCartModal } from "../../redux/Slices/CartModal";
import { useTranslation } from "react-i18next";
import { useGetAllHeaderMessagesQuery } from "../../redux/Apis/HeaderSettings";
import MobileMegaMenu from "./MobileMegaMenu";
import cartIcon from "../../shared/images/cartIcon.svg";
import { setCategories } from "../../redux/Slices/Category";
import { useGetAllCategoriesWithSubcategoriesQuery } from "../../redux/Apis/Category";
import { useGetSearchSuggestionsQuery } from "../../redux/Apis/Products";
// Lazy load CartModalRight
const CartModalRight = lazy(() => import("../Cart/CartRight"));

const Navbar = () => {
  const navbarCollapseRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isCartModalOpen = useSelector((state) => state.modal.isCartModalOpen);
  const { t } = useTranslation();
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const {
    data,
    isLoading,
  } = useGetAllHeaderMessagesQuery();
  const [visibleMessages, setVisibleMessages] = useState([]);
  // Removed collapseRef
  const [isMobileMenu, setIsMobileMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [userTyped, setUserTyped] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const { data: categories } =
    useGetAllCategoriesWithSubcategoriesQuery(
      {},
      {
        staleTime: 0,
        cacheTime: 0,
        refetchOnMountOrArgChange: true,
        refetchOnReconnect: true,
      }
    );

  // Use the search suggestions query
  const { data: searchSuggestionsData } = useGetSearchSuggestionsQuery(
    debouncedSearchTerm,
    {
      skip: !debouncedSearchTerm || debouncedSearchTerm.length <= 2,
    }
  );

  useEffect(() => {
    dispatch(setCategories(Array.isArray(categories?.data) ? categories.data : []));
  }, [categories?.data, dispatch]);

  // Handle search suggestions data
  useEffect(() => {
    if (searchSuggestionsData && searchSuggestionsData.success) {
      const { products, categories } =
        searchSuggestionsData.data.suggestions;

      const productSuggestions = products.map((p) => ({
        name: p.name,
        type: "product",
      }));

      const categorySuggestions = categories.map((c) => ({
        name: `Category: ${c.name}`,
        id: c.id,
        type: "category",
      }));

      setSuggestions([...productSuggestions, ...categorySuggestions]);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchSuggestionsData]);

  // Debounce search term
  useEffect(() => {
    if (searchTerm.length > 2 && userTyped) {
      const delayDebounce = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm);
      }, 300);

      return () => clearTimeout(delayDebounce);
    } else {
      setDebouncedSearchTerm("");
      setSuggestions([]);
      setUserTyped(false);
      setShowSuggestions(false);
    }
  }, [searchTerm, userTyped]);

  const handleSuggestionClick = (item) => {
    let searchQuery = "";

    if (item.type === "category") {
      searchQuery = `category_id=${item.id}`;
    } else {
      searchQuery = `search=${item.name}`;
    }

    setSearchTerm(item.name);
    setSuggestions([]);
    setShowSuggestions(false);
    setUserTyped(false);
    navigate(`/products?${searchQuery}`);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setUserTyped(true);

    if (value.length <= 2) {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };
  const updateMenuVisibility = () => {
    const width = window.innerWidth;
    setIsMobileMenu(width <= 991);
  };

  useEffect(() => {
    updateMenuVisibility();
    window.addEventListener("resize", updateMenuVisibility);

    return () => {
      window.removeEventListener("resize", updateMenuVisibility);
    };
  }, []);

  // Remove handleNavLinkClick logic related to collapseRef
  const handleNavLinkClick = () => {};

  useEffect(() => {
    if (isLoading) {
      // Log when the query is loading
    } else if (data && data.success) {
      const filteredMessages = Array.isArray(data.data)
        ? data.data.filter(
            (message) =>
              message.active === true && message.type === "header_message"
          )
        : [];
      setVisibleMessages(filteredMessages);
    }
  }, [data, isLoading]);

  const cartItems = useSelector((state) => state.cartSlice.items);
  const totalItemsInCart = cartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );

  const handleOutsideClick = (event) => {
    if (
      navbarCollapseRef.current &&
      !navbarCollapseRef.current.contains(event.target)
    ) {
      const isNavbarExpanded = navbarCollapseRef.current.querySelector(
        ".navbar-collapse.show"
      );
      if (isNavbarExpanded) {
        const navbarToggle = document.querySelector(".navbar-toggler");
        if (navbarToggle) {
          navbarToggle.click(); // Trigger the collapse
        }
      }
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [navbarCollapseRef]);

  const isCheckoutRoute = location.pathname === "/auth/checkout";

  const handleCartClick = () => {
    if (!isCheckoutRoute) {
      dispatch(openCartModal());
    }
  };

  useEffect(() => {
    if (visibleMessages.length > 0) {
      const interval = setInterval(() => {
        setCurrentMessageIndex(
          (prevIndex) => (prevIndex + 1) % visibleMessages.length
        );
      }, 3000); // Change message every 3 seconds

      return () => clearInterval(interval);
    }
  }, [visibleMessages]);

  return (
    <>
      <div className="">
        {isCartModalOpen && (
          <Suspense fallback={<div>Loading...</div>}>
            <CartModalRight
              isOpen={isCartModalOpen}
              onRequestClose={() => dispatch(closeCartModal())}
            />
          </Suspense>
        )}
        <div className="page-top">
          <p className="message">
            {visibleMessages?.length > 0 &&
              visibleMessages[currentMessageIndex]?.message}
          </p>
        </div>
        <div className="row p-0 m-0 py-3" style={{backgroundColor: "rgba(233, 231, 227, 1)"}}>
          <div className="col-3"></div>
          <div className="col-6 p-0 d-flex align-items-center justify-content-center">
            <Link className="navbar-brand" to="/">
              <img
                src={Logo}
                className="logo"
                alt="Logo"
                style={{ height: "100px", width: "auto" }}
              />
            </Link>
          </div>
          <div className="col-3 p-0 d-flex align-items-center justify-content-center">
            <MenuTransitions />
            <div
              className="cart-icon-container nav-icon-container"
              onClick={handleCartClick}
            >
              <img
                src={cartIcon}
                height="35px"
                className="nav-icons mt-1"
                alt=""
              />
              <div className="cart-counter">{totalItemsInCart}</div>
            </div>
          </div>
        </div>
        {/* Navigation Links Row */}
        <div className="navbar-row d-flex align-items-center justify-content-center py-2" style={{gap: '2rem',  backgroundColor: "rgba(233, 231, 227, 1)"}}>
          <ul className="navbar-nav d-flex flex-row align-items-center mb-0" style={{gap: '1.5rem'}}>
            <li className="nav-item">
              <Link
                to="/"
                className="nav-link active"
                aria-current="page"
                onClick={handleNavLinkClick}
              >
                {t("Home")}
              </Link>
            </li>
            <li className="nav-item nav-link-mega" style={{display: 'flex', alignItems: 'center'}}>
              {isMobileMenu ? (
                <MobileMegaMenu categories={categories} />
              ) : (
                <MegaMenu categories={categories} />
              )}
            </li>
            <li className="nav-item">
              <Link
                className="nav-link"
                to="/about"
                onClick={handleNavLinkClick}
              >
                {t("About")}
              </Link>
            </li>
            <li className="nav-item">
              <Link
                className="nav-link"
                to="/contact"
                onClick={handleNavLinkClick}
              >
                {t("Contact")}
              </Link>
            </li>
          </ul>
        </div>
        {/* Search Bar Row */}
        <div className="d-flex align-items-center justify-content-center py-2 pb-4" style={{gap: '1.5rem', backgroundColor: "rgba(233, 231, 227, 1)"}}>
          <div className="custom-search-container" style={{minWidth: '300px', maxWidth: '400px', width: '100%'}}>
            <input
              className="custom-search-input"
              type="text"
              inputMode="text"
              autoComplete="off"
              spellCheck="false"
              placeholder="Sök efter produkter"
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  navigate(`/products?search=${searchTerm}`);
                  setSuggestions([]);
                }
              }}
            />
            <button
              className="custom-search-button"
              onClick={() => navigate(`/products?search=${searchTerm}`)}
            >
              <SearchIcon style={{ fontSize: "1.9rem" }} />
            </button>
            {showSuggestions && suggestions.length > 0 && (
              <ul className="suggestions-list">
                {suggestions.map((item, index) => (
                  <li
                    key={index}
                    onClick={() => handleSuggestionClick(item)}
                  >
                    {item?.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
